import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db, collection, onSnapshot, query, orderBy, where, handleFirestoreError, OperationType } from '@/firebase';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, User, ArrowRight, Newspaper, Search, Clock, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDateSafe } from '@/utils/formatters';
import { Input } from '@/components/ui/input';

interface Article {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  imageUrl: string;
  category: string;
  authorName: string;
  createdAt: any;
  status: string;
}

export default function Articles() {
  const [articles, setArticles] = React.useState<Article[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('Semua');

  React.useEffect(() => {
    const q = query(
      collection(db, 'articles'), 
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const articlesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Article));
      setArticles(articlesData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'articles');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const categories = React.useMemo(() => {
    const cats = Array.from(new Set(articles.map(a => a.category).filter(Boolean)));
    return ['Semua', ...cats];
  }, [articles]);

  const filteredArticles = React.useMemo(() => {
    return articles.filter(article => {
      const matchesCategory = selectedCategory === 'Semua' || article.category === selectedCategory;
      const matchesSearch = !searchQuery.trim() || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (article.excerpt && article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (article.category && article.category.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [articles, selectedCategory, searchQuery]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <div className="animate-pulse text-gold font-bold">Memuat Artikel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-background transition-colors duration-300">
      <Helmet>
        <title>Artikel & Wawasan Konstruksi | AZMAN204</title>
        <meta name="description" content="Kumpulan artikel, tips, panduan administrasi, dan berita terbaru seputar industri konstruksi, SMKK, dan tender proyek di AZMAN204." />
        <meta property="og:title" content="Artikel & Wawasan Konstruksi | AZMAN204" />
        <meta property="og:description" content="Kumpulan artikel, tips, panduan administrasi, dan berita terbaru seputar industri konstruksi, SMKK, dan tender proyek di AZMAN204." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-gold font-black uppercase tracking-[0.3em] text-sm mb-4 block">Wawasan & Berita</span>
            <h1 className="text-4xl md:text-6xl font-black text-foreground leading-tight mb-6">
              Artikel <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">Konstruksi</span>
            </h1>
            <p className="text-foreground/60 font-medium text-lg mx-auto max-w-2xl leading-relaxed">
              Pelajari tips terbaru, berita industri, dan panduan administrasi untuk meningkatkan efisiensi proyek Anda.
            </p>
          </motion.div>

          {/* Search & Category Filter */}
          <div className="mt-10 max-w-2xl mx-auto flex flex-col gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground/40" />
              <Input
                type="text"
                placeholder="Cari artikel, panduan SMKK, tender, atau kata kunci..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 h-12 md:h-14 rounded-2xl bg-card border-border shadow-lg text-sm md:text-base focus:border-gold"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-foreground/40 hover:text-foreground"
                >
                  Reset
                </button>
              )}
            </div>

            {categories.length > 1 && (
              <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${
                      selectedCategory === cat
                        ? 'bg-gold text-navy shadow-md shadow-gold/20 scale-105'
                        : 'bg-muted/60 text-foreground/70 hover:bg-muted hover:text-foreground border border-border/50'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredArticles.length === 0 ? (
          <div className="text-center py-20 bg-muted/30 rounded-[3rem] border border-dashed border-border max-w-2xl mx-auto">
            <Newspaper className="h-16 w-16 text-foreground/20 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">
              {articles.length === 0 ? "Belum Ada Artikel" : "Artikel Tidak Ditemukan"}
            </h3>
            <p className="text-foreground/60 text-sm">
              {articles.length === 0 
                ? "Nantikan artikel menarik dari kami segera!" 
                : "Coba gunakan kata kunci pencarian lain atau pilih kategori Semua."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <Link to={`/article/${article.slug}`}>
                  <Card className="group h-full bg-card border-border hover:border-gold/30 hover:shadow-2xl hover:shadow-gold/5 transition-all duration-500 overflow-hidden rounded-[2.5rem] flex flex-col">
                    <div className="aspect-[16/10] overflow-hidden relative shrink-0">
                      <img 
                        src={article.imageUrl} 
                        alt={article.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <Badge className="absolute top-6 left-6 bg-gold text-navy font-bold border-none shadow-lg">
                        {article.category}
                      </Badge>
                    </div>
                    <CardContent className="p-6 md:p-8 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-4">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3 text-gold" />
                          {formatDateSafe(article.createdAt, { day: 'numeric', month: 'long', year: 'numeric' }, 'Terbaru')}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3 w-3 text-gold" />
                          {Math.max(1, Math.ceil((article.content || '').split(/\s+/).length / 200))} mnt
                        </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3 group-hover:text-gold transition-colors line-clamp-2 leading-tight">
                        {article.title}
                      </h3>
                      <p className="text-foreground/60 text-sm mb-6 line-clamp-3 leading-relaxed text-justify flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center text-gold text-xs font-black uppercase tracking-widest group-hover:gap-2 transition-all pt-2 border-t border-border/40">
                        Baca Selengkapnya
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
